import React from 'react';
import { useTranslation } from 'react-i18next';
import { palette } from '../../constants/colors';
import { hexToRgba } from '../../utils/chartHelpers';
import ChartFrame from './ChartFrame';

export interface HeatmapChartProps {
    rows?: string[];
    cols?: string[];
    values?: number[][];
    colorFrom?: string;
    colorTo?: string;
    showLegend?: boolean;
    className?: string;
    title?: string;
    badge?: string;
}

const emptyMatrix = (rowCount: number, colCount: number): number[][] =>
    Array.from({ length: rowCount }, () => Array.from({ length: colCount }, () => 0));

const HeatmapChart: React.FC<HeatmapChartProps> = ({
    rows = [],
    cols = [],
    values,
    colorFrom = palette.softBlue,
    colorTo = palette.primary,
    showLegend = true,
    className,
    title,
    badge,
}) => {
    const { t } = useTranslation('charts');
    const matrix = React.useMemo(() => {
        if (Array.isArray(values) && values.length > 0) return values;
        if (rows.length > 0 && cols.length > 0) return emptyMatrix(rows.length, cols.length);
        return [];
    }, [rows, cols, values]);

    const max = React.useMemo(() => {
        let m = 0;
        for (const row of matrix) {
            for (const v of row) if (v > m) m = v;
        }
        return m || 1;
    }, [matrix]);

    const cellColor = (v: number): string => {
        const t = Math.max(0, Math.min(1, v / max));
        return hexToRgba(colorTo, Math.max(0.08, t));
    };

    const visibleHourTicks = ['00', '03', '06', '09', '12', '15', '18', '21'];

    if (rows.length === 0 || cols.length === 0 || matrix.length === 0) {
        return null;
    }

    return (
        <ChartFrame title={title} badge={badge} className={className}>
        <div className="w-full h-full flex flex-col overflow-hidden">
            <div className="flex-1 flex flex-col overflow-x-auto overflow-y-hidden custom-scrollbar">
                <div className="min-w-[400px] flex-1 flex flex-col">
                    <div className="flex mb-1 pl-8">
                        {cols.map((h) => (
                            <div key={h} className="flex-1 text-[8px] font-bold text-gray-400 text-center">
                                {visibleHourTicks.includes(h) ? h : ''}
                            </div>
                        ))}
                    </div>
                    <div className="flex-1 flex flex-col gap-[2px]">
                        {rows.map((rowLabel, dIdx) => (
                            <div key={rowLabel} className="flex flex-1 gap-[2px]">
                                <div className="w-8 text-[9px] font-bold text-gray-500 flex items-center">{rowLabel}</div>
                                {cols.map((hour, hIdx) => {
                                    const v = matrix[dIdx]?.[hIdx] ?? 0;
                                    const intense = v / max > 0.6;
                                    return (
                                        <div
                                            key={hour}
                                            className="flex-1 rounded-[1px] flex items-center justify-center"
                                            style={{ backgroundColor: cellColor(v) }}
                                        >
                                            <span className={`text-[7px] font-bold ${intense ? 'text-white/90' : 'text-primary-600/40'}`}>
                                                {Math.floor(v / 4)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {showLegend && (
                <div className="mt-4 flex items-center gap-2">
                    <span className="text-[9px] font-medium text-gray-400">{t('heatmap.low')}</span>
                    <div
                        className="w-24 h-1.5 rounded-full"
                        style={{ background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}
                    />
                    <span className="text-[9px] font-medium text-gray-400">{t('heatmap.high')}</span>
                </div>
            )}
        </div>
        </ChartFrame>
    );
};

export default HeatmapChart;
